<template>
  <AdminLayout>
    <div class="d-flex flex-column">
      <div class="d-flex flex-row align-center mb-3">
        <h1 class="outfit-regular">Dashboard</h1>
        <v-spacer></v-spacer>
        <v-btn color="black" icon dark @click="refreshAll" :loading="loadingAll">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </div>

      <v-card outlined class="pa-4 mb-6">
        <div class="d-flex flex-row align-center justify-space-between mb-2">
          <div class="subtitle-1 font-weight-medium">New users per day (last {{ daysWindow }} days)</div>
          <div class="caption grey--text">Total: {{ totalUsers | formatNumber }}</div>
        </div>
        <div class="d-flex flex-column">
          <v-sparkline
            :value="usersDaily.values"
            :smooth="8"
            :gradient="['#FFD54F', '#FFC107']"
            :line-width="3"
            :padding="12"
            auto-draw
          ></v-sparkline>
          <div class="d-flex flex-row justify-space-between mt-2 caption grey--text text--darken-1" style="overflow-x: auto; white-space: nowrap;">
            <div v-for="(label, idx) in usersDaily.labels" :key="idx" class="mr-2">{{ label }}</div>
          </div>
        </div>
      </v-card>

      <div class="d-flex flex-row flex-wrap">
        <v-card
          v-for="k in kpiList"
          :key="k.key"
          outlined
          class="pa-4 mr-4 mb-4"
          style="min-width: 260px; flex: 1 1 260px;"
        >
          <div class="d-flex flex-row align-center justify-space-between">
            <div class="subtitle-1 font-weight-medium">{{ k.label }}</div>
            <v-icon small class="grey--text text--darken-1">{{ k.icon }}</v-icon>
          </div>
          <div class="d-flex flex-row align-end mt-3">
            <div class="display-1 font-weight-bold mr-3">{{ ((metrics[k.key] && metrics[k.key].total) || 0) | formatNumber }}</div>
            <div class="d-flex flex-row align-center" :class="(((metrics[k.key] && metrics[k.key].yesterday) || 0) > 0) ? 'green--text text--darken-2' : 'grey--text text--darken-1'">
              <v-icon small class="mr-1">{{ (((metrics[k.key] && metrics[k.key].yesterday) || 0) > 0) ? 'mdi-arrow-up-bold' : 'mdi-minus' }}</v-icon>
              <span class="subtitle-2">{{ (((metrics[k.key] && metrics[k.key].yesterday) || 0)) | formatNumber }}</span>
            </div>
          </div>
          <div class="caption grey--text text--darken-1 mt-1">yesterday</div>
        </v-card>
      </div>

      <div class="d-flex flex-row flex-wrap mt-2">
        <v-card outlined class="pa-4 mr-4 mb-4" style="min-width: 320px; flex: 1 1 320px;">
          <div class="subtitle-1 font-weight-medium mb-2">Recent signups</div>
          <div v-if="recentUsers.length === 0" class="caption grey--text">No recent users</div>
          <div v-for="u in recentUsers" :key="u.id" class="d-flex flex-row align-center py-1" style="border-bottom: 1px dashed rgba(0,0,0,0.06);">
            <v-icon small class="mr-2">mdi-account</v-icon>
            <div class="d-flex flex-column">
              <div class="subtitle-2">{{ u.name || u.email || u.id }}</div>
              <div class="caption grey--text">{{ formatDateTime(u.createdAt) }}</div>
            </div>
          </div>
        </v-card>
      </div>
    </div>
  </AdminLayout>
</template>

<script>
import { db } from '@/firebase/init'
import { collection, getCountFromServer, getDocs, query, where, orderBy, limit } from 'firebase/firestore'

import AdminLayout from '@/layouts/Admin.vue'
export default {

  name: 'AdminDashboard',

  components: { AdminLayout },
  
  data() {
    return {
      loadingAll: false,
      daysWindow: 30,
      totalUsers: 0,
      usersDaily: { labels: [], values: [] },
      metrics: {
        users: { total: 0, yesterday: 0 },
        stories: { total: 0, yesterday: 0 },
        ideas: { total: 0, yesterday: 0 },
        lessons: { total: 0, yesterday: 0 },
        games: { total: 0, yesterday: 0 },
        conundrums: { total: 0, yesterday: 0 }
      },
      recentUsers: []
    }
  },
  created() {
    this.refreshAll()
  },
  computed: {
    kpiList() {
      return [
        { key: 'users', label: 'Users', icon: 'mdi-account-multiple' },
        { key: 'stories', label: 'Stories', icon: 'mdi-book-open-variant' },
        { key: 'ideas', label: 'Ideas', icon: 'mdi-lightbulb-on-outline' },
        { key: 'lessons', label: 'Lessons', icon: 'mdi-school' },
        { key: 'games', label: 'Games', icon: 'mdi-gamepad-variant' },
        { key: 'conundrums', label: 'Conundrums', icon: 'mdi-help-rhombus' }
      ]
    }
  },
  methods: {
    async refreshAll() {
      if (this.loadingAll) return
      this.loadingAll = true
      try {
        await Promise.all([
          this.loadUsersTrend(this.daysWindow),
          this.loadRecentUsers(5),
          this.loadCollectionMetrics('users'),
          this.loadCollectionMetrics('stories'),
          this.loadCollectionMetrics('ideas'),
          this.loadCollectionMetrics('lessons'),
          this.loadCollectionMetrics('games'),
          this.loadCollectionMetrics('conundrums')
        ])
      } catch (e) {
        console.error('Dashboard refresh error', e)
      } finally {
        this.loadingAll = false
      }
    },

    startOfDay(d) {
      const x = new Date(d)
      x.setHours(0, 0, 0, 0)
      return x
    },
    endOfDay(d) {
      const x = new Date(d)
      x.setHours(23, 59, 59, 999)
      return x
    },
    shiftDays(d, delta) {
      const x = new Date(d)
      x.setDate(x.getDate() + delta)
      return x
    },

    async loadUsersTrend(days = 30) {
      const today = this.startOfDay(new Date())
      const windowStart = this.shiftDays(today, -days + 1)
      const usersRef = collection(db, 'users')
      const qy = query(usersRef, where('createdAt', '>=', windowStart))
      const snap = await getDocs(qy)
      const buckets = new Map()
      for (let i = 0; i < days; i++) {
        const d = this.shiftDays(windowStart, i)
        const key = d.toISOString().slice(0, 10)
        buckets.set(key, 0)
      }
      snap.docs.forEach(doc => {
        const data = doc.data() || {}
        const ts = data.createdAt && data.createdAt.toDate ? data.createdAt.toDate() : data.createdAt
        if (!ts) return
        const dayKey = this.startOfDay(ts).toISOString().slice(0, 10)
        if (buckets.has(dayKey)) buckets.set(dayKey, (buckets.get(dayKey) || 0) + 1)
      })
      const labels = Array.from(buckets.keys()).map(k => k.slice(5))
      const values = Array.from(buckets.values())
      this.usersDaily = { labels, values }
      const countSnap = await getCountFromServer(usersRef)
      this.totalUsers = countSnap.data().count || 0
      this.metrics.users.total = this.totalUsers
      const startY = this.shiftDays(today, -1)
      const endY = today
      const startDBY = this.shiftDays(today, -2)
      const endDBY = startY
      const qyY = query(usersRef, where('createdAt', '>=', startY), where('createdAt', '<', endY))
      const qyDBY = query(usersRef, where('createdAt', '>=', startDBY), where('createdAt', '<', endDBY))
      const [ySnap, dbySnap] = await Promise.all([getDocs(qyY), getDocs(qyDBY)])
      // Show how many were added yesterday (non-negative), not the diff vs the day before
      this.metrics.users.yesterday = ySnap.size
    },

    async loadCollectionMetrics(collectionName) {
      const colRef = collection(db, collectionName)
      try {
        const totalSnap = await getCountFromServer(colRef)
        if (!this.metrics[collectionName]) this.$set(this.metrics, collectionName, { total: 0, yesterday: 0 })
        this.metrics[collectionName].total = totalSnap.data().count || 0

        const today = this.startOfDay(new Date())
        const startY = this.shiftDays(today, -1)
        const startDBY = this.shiftDays(today, -2)
        const qyY = query(colRef, where('createdAt', '>=', startY), where('createdAt', '<', today))
        const qyDBY = query(colRef, where('createdAt', '>=', startDBY), where('createdAt', '<', startY))
        const [ySnap, dbySnap] = await Promise.all([getDocs(qyY), getDocs(qyDBY)])
        // Show how many were added yesterday (non-negative), not the diff vs the day before
        this.metrics[collectionName].yesterday = ySnap.size
      } catch (e) {
        console.warn(`Failed to load metrics for ${collectionName}`, e)
        if (!this.metrics[collectionName]) this.$set(this.metrics, collectionName, { total: 0, yesterday: 0 })
      }
    },

    async loadRecentUsers(maxItems = 5) {
      try {
        const usersRef = collection(db, 'users')
        const qy = query(usersRef, orderBy('createdAt', 'desc'), limit(maxItems))
        const snap = await getDocs(qy)
        this.recentUsers = snap.docs.map(d => ({ id: d.id, ...d.data() }))
      } catch (e) {
        console.warn('Failed to load recent users', e)
        this.recentUsers = []
      }
    },

    formatDateTime(v) {
      const dt = v && v.toDate ? v.toDate() : v
      if (!dt) return ''
      return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }).format(dt)
    }
  },
  filters: {
    formatNumber(v) {
      try { return new Intl.NumberFormat().format(Number(v || 0)) } catch (e) { return v }
    }
  }
}
</script>